<h1>{{ isEditMode ? 'Edit Print Attribute' : 'Create Print Attribute' }}</h1>

<form [formGroup]="formGroup" novalidate>
  <mat-card appearance="outlined">
    <mat-card-content fxLayout="column" fxLayoutGap.gt-sm="16px">
      <!-- Image Upload Section - For image-required attribute types (mockup: ce-print-attr-detail) -->
      <div *ngIf="selectedAssetPreview as preview" fxFlex="100" fxLayout="column" fxLayoutGap="8px" class="field-spacer image-top-section">
        <div
          class="image-upload-container"
          [class.image-upload-container--preview]="selectedAssetPreview"
        >

          <!-- Selected asset preview -->
          <ng-container>
            <div
              *ngIf="!preview.FileUrl || previewImageLoadFailed"
              class="asset-preview-plate"
              [style.background-color]="preview.FileUrl ? '#' + preview.FileUrl.substring(0, 6) : '#ffffff'"
            >
              <mat-icon class="asset-preview-plate-icon">image</mat-icon>
              <span class="asset-preview-name">{{ preview.AssetName }}</span>
              <span class="asset-preview-meta">{{ preview.FileType }}</span>
            </div>
            <img
              *ngIf="preview.FileUrl && !previewImageLoadFailed"
              class="asset-preview-img"
              [src]="preview.FileUrl"
              [alt]="preview.AssetName"
              (error)="onAssetPreviewImageError()"
            />
          </ng-container>
        </div>

        <div *ngIf="selectedAssetPreview" class="img-preview-actions" fxLayout="row" fxLayoutGap="8px">
          <button mat-stroked-button type="button" (click)="addAssetsToForm()">
            <mat-icon>upload</mat-icon>
            Replace
          </button>
        </div>
      </div>
      <div *ngIf="showImageUpload && !isEditMode && !selectedAssetPreview" fxLayout="column" fxLayoutGap="4px" class="image-upload-toolbar">
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px">
          <button mat-stroked-button color="primary" type="button" (click)="addAssetsToForm(); $event.stopPropagation()">
            <mat-icon>folder_open</mat-icon>
            Add from Assets
          </button>
        </div>
        <mat-error
          *ngIf="formGroup.get('AssetId')?.hasError('required') && (formGroup.get('AssetId')?.touched || formGroup.get('AssetId')?.dirty)"
        >
          Asset is required
        </mat-error>
      </div>

      <!-- Top section: two-column rows like ce-print-attr-detail + create-custom-field -->
      <div fxFlex="100" fxLayout="column" fxLayoutGap.gt-sm="16px">
        <div fxLayout="row wrap" fxLayoutGap.gt-sm="16px" formGroupName="Detail">
          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Attribute Type</mat-label>
            <mat-select formControlName="PrintAttributeType">
              <mat-option value="">Select type...</mat-option>
              <mat-option *ngFor="let type of attributeTypeOptions" [value]="type.id">
                {{ type.label }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="detailForm.get('PrintAttributeType')?.hasError('required')">
              Attribute Type is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Name</mat-label>
            <input matInput formControlName="PrintAttributeName" placeholder="Enter attribute name" />
            <mat-error *ngIf="detailForm.get('PrintAttributeName')?.hasError('required')">
              Name is required
            </mat-error>
          </mat-form-field>
        </div>

        <div fxLayout="row wrap" fxLayoutGap.gt-sm="16px">
          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Event Type</mat-label>
            <mat-select formControlName="EventTypeId" required>
              <mat-option value="">Select...</mat-option>
              <mat-option *ngFor="let type of eventTypes" [value]="type.id">
                {{ type.name }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="formGroup.get('EventTypeId')?.hasError('required') && (formGroup.get('EventTypeId')?.touched || formGroup.get('EventTypeId')?.dirty)">
              Event Type is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Division</mat-label>
            <mat-select formControlName="DivisionIds" multiple (selectionChange)="onDivisionChange($event)">
              <mat-option *ngFor="let division of divisions" [value]="division.id">
                {{ division.name }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="formGroup.get('DivisionIds')?.hasError('required')">
              Division is required
            </mat-error>
          </mat-form-field>
        </div>

        <div fxLayout="row wrap" fxLayoutGap.gt-sm="16px">
          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Banner</mat-label>
            <mat-select formControlName="BannerId">
              <mat-option value="">—</mat-option>
              <mat-option *ngFor="let banner of banners" [value]="banner.id">
                {{ banner.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div fxFlex="49" fxFlex.lt-md="100" formGroupName="Detail">
            <mat-form-field appearance="outline">
              <mat-label>Logical Page</mat-label>
              <input matInput formControlName="LogicalPrintPage" placeholder="e.g., TV01" />
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Detail: Version + Description / Attribute Data -->
      <div fxFlex="100" fxLayout="column" fxLayoutGap.gt-sm="16px" formGroupName="Detail">
        <mat-form-field appearance="outline" fxFlex="100" fxFlex.lt-md="100">
          <mat-label>Version</mat-label>
          <mat-chip-grid #versionsChipGrid aria-label="Versions" class="versions-chip-grid">
            <mat-chip-row
              *ngFor="let ver of versionsChipList; let i = index"
              (removed)="onVersionChipRemoved(i)"
            >
              {{ ver }}
              <button type="button" matChipRemove aria-label="Remove version" class="versions-chip-remove">
                <span aria-hidden="true" class="versions-chip-remove-glyph">&times;</span>
              </button>
            </mat-chip-row>
            <input
              placeholder="e.g., CE201 — Enter or comma to add"
              [matChipInputFor]="versionsChipGrid"
              [matChipInputSeparatorKeyCodes]="versionChipSeparatorKeysCodes"
              (matChipInputTokenEnd)="onVersionChipInputEnd($event)"
            />
          </mat-chip-grid>
        </mat-form-field>

        <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap.gt-sm="16px">
          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Client Key</mat-label>
            <input matInput formControlName="ClientKey" placeholder="e.g., TV01" />
            <mat-error *ngIf="detailForm.get('ClientKey')?.hasError('required')">
              Client Key is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" fxFlex="49" fxFlex.lt-md="100">
            <mat-label>Status</mat-label>
            <mat-select formControlName="Status">
              <mat-option value="active">Active</mat-option>
              <mat-option value="inactive">Inactive</mat-option>
            </mat-select>
            <mat-error *ngIf="detailForm.get('Status')?.hasError('required')">
              Status is required
            </mat-error>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" fxFlex="100" fxFlex.lt-md="100">
          <mat-label>Description</mat-label>
          <textarea matInput formControlName="Description" placeholder="Instructional text explaining what needs to change..." rows="4"></textarea>
        </mat-form-field>

        <div *ngIf="dynamicFields?.length" fxFlex="100" class="field-spacer" formGroupName="Data">
          <p class="section-title">Attribute Data — {{ selectedAttributeType }}</p>

          <ng-template #remainingDynamicFieldsTpl>
            <ng-container *ngFor="let field of remainingDynamicFields">
              <mat-form-field
                *ngIf="field.type === 'date'"
                appearance="outline"
                [fxFlex]="flexForRemainingField()"
                fxFlex.lt-md="100"
              >
                <mat-label>{{ field.label }}</mat-label>
                <input
                  matInput
                  [matDatepicker]="fieldDatePicker"
                  [formControlName]="field.id"
                />
                <mat-datepicker-toggle matSuffix [for]="fieldDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #fieldDatePicker></mat-datepicker>
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('required')">
                  {{ field.label }} is required
                </mat-error>
                <mat-error
                  *ngIf="
                    field.id === 'SaleEndDate' &&
                    dataFieldsForm.invalid &&
                    dataFieldsForm.hasError('saleDateRange')
                  "
                >
                  Sale start must be on or before sale end date.
                </mat-error>
              </mat-form-field>

              <mat-form-field
                *ngIf="field.type !== 'date' && field.type !== 'textarea'"
                appearance="outline"
                [fxFlex]="flexForRemainingField()"
                fxFlex.lt-md="100"
              >
                <mat-label>{{ field.label }}</mat-label>
                <input
                  matInput
                  [type]="field.type"
                  [formControlName]="field.id"
                  [readonly]="!!field.readOnly"
                  [placeholder]="field.placeholder || ('Enter ' + (field.label | lowercase))"
                />
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('required')">
                  {{ field.label }} is required
                </mat-error>
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('pattern') && field.type === 'url'">
                  Must start with http:// or https://
                </mat-error>
              </mat-form-field>

              <mat-form-field
                *ngIf="field.type === 'textarea'"
                appearance="outline"
                [fxFlex]="flexForRemainingField()"
                fxFlex.lt-md="100"
              >
                <mat-label>{{ field.label }}</mat-label>
                <textarea
                  matInput
                  [formControlName]="field.id"
                  [placeholder]="field.placeholder || ('Enter ' + (field.label | lowercase))"
                  rows="3"
                ></textarea>
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('required')">
                  {{ field.label }} is required
                </mat-error>
              </mat-form-field>
            </ng-container>
          </ng-template>

          <div *ngIf="assetPairFields as pair" class="dynamic-fields-row--two-cols dynamic-fields-asset-row">
            <ng-container *ngFor="let field of pair">
              <mat-form-field appearance="outline">
                <mat-label>{{ field.label }}</mat-label>
                <input
                  matInput
                  [type]="field.type"
                  [formControlName]="field.id"
                  [readonly]="!!field.readOnly"
                  [placeholder]="field.placeholder || ('Enter ' + (field.label | lowercase))"
                />
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('required')">
                  {{ field.label }} is required
                </mat-error>
                <mat-error *ngIf="getDynamicFieldControl(field.id).hasError('pattern') && field.type === 'url'">
                  Must start with http:// or https://
                </mat-error>
              </mat-form-field>
            </ng-container>
          </div>

          <ng-container *ngIf="remainingDynamicFields.length">
            <div
              *ngIf="isRemainingTwoColumn(); else remainingFieldsFlexWrap"
              class="dynamic-fields-row--two-cols dynamic-fields-rest-row"
            >
              <ng-container *ngTemplateOutlet="remainingDynamicFieldsTpl"></ng-container>
            </div>
            <ng-template #remainingFieldsFlexWrap>
              <div
                class="dynamic-fields-rest-row"
                fxLayout="row wrap"
                fxLayoutGap.gt-sm="16px"
                fxLayoutAlign="flex-start stretch"
              >
                <ng-container *ngTemplateOutlet="remainingDynamicFieldsTpl"></ng-container>
              </div>
            </ng-template>
          </ng-container>
        </div>

      </div>
    </mat-card-content>

    <mat-card-actions fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="15px">
      <div fxLayout="row" [fxLayoutAlign]="isEditMode ? 'space-between' : 'end'" class="mat-card-actions-details">
        <button 
          *ngIf="isEditMode && !readOnlyMode" 
          mat-raised-button 
          type="button" 
          color="warn" 
          (click)="deletePrintAttribute()">
          Delete
        </button>
        <div fxLayout="row" fxLayoutAlign="end start" fxLayoutGap="16px">
          <button 
            mat-stroked-button 
            type="button" 
            (click)="cancel()">
            Close
          </button>
          <button 
            mat-raised-button 
            type="button" 
            (click)="savePrintAttribute()" 
            [disabled]="!formGroup.valid || readOnlyMode" 
            color="accent">
            Save
          </button>
        </div>
      </div>
    </mat-card-actions>
  </mat-card>
</form>
